import { useCallback, useMemo, useState } from 'react';
import { fileLinkMessage } from '../domain/fileLinks';
import { LINK_UNAVAILABLE } from '../data/fileLinks';
import { copyLink } from '../lib/copyLink';

// Open a stored file, or copy its link, and say what happened (#515).
//
// Both go through the studio's link cache (`linkFor`), so both hand out the
// same thirty-day link of ours: Open puts it in a new tab, which then follows
// the redirect to the hour-long storage URL; Copy link puts *our* link on the
// clipboard, which is the one worth sending. After a copy the teacher is told
// how long it lasts — `copied` in core/src/file_links.rs, shared with both
// phones.
//
// `linkFor` is passed in rather than read from a provider so the receipts on
// an expense (useExpenses' `attachmentUrl`) and the library (useFiles'
// `linkFor`) share this without either depending on the other.
export const COPY_REFUSED = 'This browser did not let the link be copied. Please try again.';

export default function useResourceActions(linkFor) {
  const [notice, setNotice] = useState(null);

  const open = useCallback((path) => {
    if (!path || !linkFor) return;
    // After the link rather than before it, as the receipt's View already
    // does: a link the cache holds resolves at once, and one it has to ask
    // for is a round trip browsers allow inside the click's activation.
    linkFor(path).then(
      (url) => { if (url) window.open(url, '_blank', 'noopener'); },
      (err) => setNotice({ kind: 'error', text: err?.message || LINK_UNAVAILABLE }),
    );
  }, [linkFor]);

  const copy = useCallback((path) => {
    if (!path || !linkFor) return;
    let linkFailed = false;
    const link = linkFor(path).then(null, (err) => { linkFailed = true; throw err; });
    copyLink(link).then(
      () => setNotice({ kind: 'ok', text: fileLinkMessage('copied') }),
      // Two failures that look alike from here, told apart by which promise
      // failed: no link to copy (the callable's sentence), or a browser that
      // refused the clipboard write (whose own message is written for a
      // developer, so it is not shown).
      (err) => setNotice({ kind: 'error', text: linkFailed ? (err?.message || LINK_UNAVAILABLE) : COPY_REFUSED }),
    );
  }, [linkFor]);

  return useMemo(() => ({
    open, copy, notice, clearNotice: () => setNotice(null),
  }), [open, copy, notice]);
}
